// Top of the order page: a progress tracker (Placed → Confirmed → Paid → Shipped → Delivered) and ONE
// plain sentence about what happens next — with the one action that moves the order forward for this
// viewer. Clients get "Pay A$X now"; reps see who owes what (and can copy the payment link); staff
// get the approval steps (confirm shipping → approve & email invoice).
import { useEffect, useRef, useState } from 'react';
import { fetchOrderPayments, generateLink, fetchSavedCards, payWithSavedCard } from '../api/payments.js';
import { fmtMoney } from '../lib/matrix.js';
import { orderProgress, installmentTiming } from '../lib/labels.js';

const errMsg = (e) => e.response?.data?.error?.message || 'Something went wrong — please try again.';
// "Balance (30 days after dispatch)" → "Balance": the timing is said separately, in plain words.
const shortLabel = (label) => String(label || 'Payment').replace(/\s*\(.*\)\s*$/, '');

function Stepper({ progress }) {
  if (!progress) return null;
  return (
    <ol className="flex flex-wrap items-center gap-x-1 gap-y-2" aria-label="Order progress">
      {progress.steps.map((s, i) => {
        const current = i === progress.current;
        return (
          <li key={s.key} className="flex items-center gap-1">
            <span
              className={`flex h-6 min-w-6 items-center justify-center rounded-full px-1.5 text-[11px] font-semibold ${
                s.done ? 'bg-success text-white' : current ? 'bg-accent text-white' : 'bg-surface-raised text-text-muted'
              }`}
              aria-hidden
            >
              {s.done ? '✓' : i + 1}
            </span>
            <span className={`text-xs ${s.done ? 'text-text-primary' : current ? 'font-semibold text-text-primary' : 'text-text-muted'}`}>{s.label}</span>
            {i < progress.steps.length - 1 && <span className={`mx-1 h-px w-5 sm:w-8 ${s.done ? 'bg-success' : 'bg-line'}`} aria-hidden />}
            {current && <span className="sr-only">(current step)</span>}
          </li>
        );
      })}
    </ol>
  );
}

export default function OrderNextStep({ order, role, refreshKey, onPaid, staffApproval }) {
  const isClient = role === 'CLIENT';
  const isRep = role === 'REP';
  const [payments, setPayments] = useState(null);
  const [cards, setCards] = useState([]);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const inFlight = useRef(false);

  useEffect(() => {
    fetchOrderPayments(order.id).then(setPayments).catch(() => setPayments(null));
    if (isClient) fetchSavedCards(order.id).then(setCards).catch(() => setCards([]));
  }, [order.id, refreshKey, isClient]);

  const progress = orderProgress(order, payments);
  const due = (payments?.installments || []).find((i) => i.status !== 'PAID' && i.status !== 'VOID' && i.activatedAt);
  const cur = order.currency;
  const card = cards[0];

  const run = async (key, fn) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(key);
    setError('');
    try {
      await fn();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      inFlight.current = false;
      setBusy('');
    }
  };
  const payNow = () =>
    run('pay', async () => {
      let url = due.linkUrl;
      if (!url) url = (await generateLink(due.id)).installment?.linkUrl;
      if (url) window.open(url, '_blank', 'noopener');
    });
  const payCard = () => {
    if (!window.confirm(`Charge ${fmtMoney(due.amount, due.currency || cur)} to your ${card.brand || 'card'} ending ${card.last4}?`)) return;
    run('card', async () => {
      await payWithSavedCard(due.id, card.id);
      onPaid?.();
    });
  };
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(due.linkUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt('Copy the payment link:', due.linkUrl);
    }
  };

  // --- What happens next (one sentence + at most one primary action) ---
  let tone = 'info';
  let message = null;
  let action = null;
  const who = isClient ? 'you' : order.company || 'the client';
  if (order.status === 'REJECTED') {
    tone = 'muted';
    message = isClient ? 'We couldn’t accept this order. Check your email for the reason, or message us — we’re happy to help.' : 'This order was not accepted.';
  } else if (order.status === 'CANCELLED') {
    tone = 'muted';
    message = 'This order was cancelled.';
  } else if (order.status === 'PENDING_APPROVAL') {
    if (staffApproval) {
      message = null; // staff get the approval checklist instead
    } else {
      message = isClient
        ? 'We’re reviewing your order and confirming shipping. You’ll get your invoice and a payment link by email — usually within one business day.'
        : `TEAMM8 is reviewing this order and confirming shipping. ${order.company || 'The client'} will get the invoice and payment link by email.`;
    }
  } else if (due) {
    tone = due.dueDate && new Date(due.dueDate).getTime() < Date.now() ? 'warn' : 'action';
    const amount = fmtMoney(due.amount, due.currency || cur);
    message = isClient
      ? `${shortLabel(due.label)}: ${amount} — ${installmentTiming(due).toLowerCase()}.`
      : `Waiting for ${who} to pay the ${shortLabel(due.label).toLowerCase()} of ${amount} (${installmentTiming(due).toLowerCase()}).`;
    if (isClient) {
      action = (
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={payNow} disabled={busy === 'pay'} className="rounded-md bg-accent px-4 py-2 text-sm font-semibold text-white hover:bg-accent-hover disabled:opacity-50">
            {busy === 'pay' ? 'Opening…' : `Pay ${amount} now`}
          </button>
          {card && (
            <button onClick={payCard} disabled={busy === 'card'} className="rounded-md border border-accent px-3 py-2 text-sm font-medium text-accent hover:bg-accent-light disabled:opacity-50">
              {busy === 'card' ? 'Charging…' : `Pay with ${card.brand ? card.brand.replace(/^\w/, (m) => m.toUpperCase()) : 'card'} ••${card.last4}`}
            </button>
          )}
        </div>
      );
    } else if (isRep && due.linkUrl) {
      action = (
        <button onClick={copyLink} className="rounded-md border border-line bg-surface px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-raised">
          {copied ? 'Copied ✓' : 'Copy payment link'}
        </button>
      );
    }
  } else if (order.status === 'APPROVED') {
    const shipped = progress?.steps.find((s) => s.key === 'shipped')?.done;
    const delivered = progress?.steps.find((s) => s.key === 'delivered')?.done;
    const nextBalance = (payments?.installments || []).find((i) => i.status !== 'PAID' && i.status !== 'VOID');
    tone = 'success';
    if (delivered) message = nextBalance ? `Delivered. ${shortLabel(nextBalance.label)} (${fmtMoney(nextBalance.amount, nextBalance.currency || cur)}) is ${installmentTiming(nextBalance).toLowerCase()}.` : 'Delivered — all done. Thank you!';
    else if (shipped) message = `On its way${order.trackingNumbers?.length ? ' — see tracking below' : ''}.${nextBalance ? ` ${shortLabel(nextBalance.label)} is ${installmentTiming(nextBalance).toLowerCase()}.` : ''}`;
    else message = nextBalance ? `Confirmed — being prepared for dispatch. ${shortLabel(nextBalance.label)} (${fmtMoney(nextBalance.amount, nextBalance.currency || cur)}) is ${installmentTiming(nextBalance).toLowerCase()}.` : 'Confirmed and paid — being prepared for dispatch.';
  }

  const toneCls = {
    info: 'border-accent/30 bg-accent-light/40',
    action: 'border-accent/40 bg-accent-light/60',
    warn: 'border-error/40 bg-error-light/60',
    success: 'border-success/30 bg-success-light/50',
    muted: 'border-line bg-surface-raised/50',
  }[tone];

  return (
    <div className="mb-4 space-y-3 rounded-lg border border-line bg-surface p-4 shadow-sm">
      <Stepper progress={progress} />
      {staffApproval}
      {message && (
        <div className={`flex flex-wrap items-center justify-between gap-3 rounded-md border px-3 py-2.5 ${toneCls}`}>
          <p className="text-sm text-text-primary">{message}</p>
          {action}
        </div>
      )}
      {error && <p className="text-xs text-error">{error}</p>}
    </div>
  );
}
